/**
 * useEntityAuditLogs - Hook for fetching entity-specific audit logs
 * Uses cursor-based pagination following industry best practices
 */

import { useState, useEffect, useCallback } from 'react';
import type { AuditLog, AuditLogSubItem } from '@/types';
import {
  getEntityAuditLogs,
  getAuditLogDetails,
  type EntityAuditResponse,
} from '@/lib/adminApi';

type EntityType = 'company' | 'group' | 'unit' | 'user' | 'site';

interface UseEntityAuditLogsOptions {
  /** Entity type to fetch logs for */
  entityType: EntityType;
  /** Entity UUID */
  entityId: string;
  /** Number of logs per page (default: 10) */
  pageSize?: number;
  /** Auto-fetch on mount (default: true) */
  autoFetch?: boolean;
}

interface UseEntityAuditLogsReturn {
  // Data
  logs: AuditLog[];
  total: number;
  hasMore: boolean;

  // Loading states
  isLoading: boolean;
  isLoadingMore: boolean;

  // Error state
  error: string | null;

  // Actions
  fetchLogs: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Expandable details
  expandedLogIds: Set<string>;
  toggleExpanded: (logId: string) => void;
  loadLogDetails: (logId: string) => Promise<AuditLogSubItem[]>;
}

export function useEntityAuditLogs(
  options: UseEntityAuditLogsOptions
): UseEntityAuditLogsReturn {
  const { entityType, entityId, pageSize = 10, autoFetch = true } = options;

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Start loading if we have an entityId and autoFetch is enabled
  const [isLoading, setIsLoading] = useState(autoFetch && !!entityId);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(new Set());

  // Initial fetch
  const fetchLogs = useCallback(async () => {
    if (!entityId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response: EntityAuditResponse = await getEntityAuditLogs(
        entityType,
        entityId,
        { limit: pageSize }
      );

      setLogs(response.logs);
      setTotal(response.total);
      setHasMore(response.hasMore);
      setNextCursor(response.nextCursor);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load audit logs';
      setError(message);
      console.error('Failed to fetch entity audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId, pageSize]);

  // Load more (cursor-based pagination)
  const loadMore = useCallback(async () => {
    if (!entityId || !hasMore || !nextCursor || isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      const response: EntityAuditResponse = await getEntityAuditLogs(
        entityType,
        entityId,
        { limit: pageSize, cursor: nextCursor }
      );

      setLogs(prev => [...prev, ...response.logs]);
      setHasMore(response.hasMore);
      setNextCursor(response.nextCursor);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more logs';
      setError(message);
      console.error('Failed to load more audit logs:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [entityType, entityId, pageSize, hasMore, nextCursor, isLoadingMore]);

  // Refresh (reset and fetch from beginning)
  const refresh = useCallback(async () => {
    setLogs([]);
    setNextCursor(null);
    setExpandedLogIds(new Set());
    await fetchLogs();
  }, [fetchLogs]);

  // Toggle expanded state for a log
  const toggleExpanded = useCallback((logId: string) => {
    setExpandedLogIds(prev => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  }, []);

  // Load details for a specific log (lazy loading)
  const loadLogDetails = useCallback(async (logId: string): Promise<AuditLogSubItem[]> => {
    try {
      const response = await getAuditLogDetails(logId);

      // Update the log in state with its details
      setLogs(prev =>
        prev.map(log =>
          log.id === logId ? { ...log, subItems: response.details } : log
        )
      );

      return response.details;
    } catch (err) {
      console.error('Failed to load audit log details:', err);
      return [];
    }
  }, []);

  // Auto-fetch on mount and when entityId changes
  useEffect(() => {
    if (autoFetch && entityId) {
      fetchLogs();
    }
  }, [autoFetch, entityId, fetchLogs]);

  return {
    logs,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    fetchLogs,
    loadMore,
    refresh,
    expandedLogIds,
    toggleExpanded,
    loadLogDetails,
  };
}
